import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/lib/data';
import { DEFAULT_SYMPTOMS } from '@/lib/defaults';
import {
  Battery, Leaf, Moon, Scale, Brain, Sparkles, Heart, Shield,
  ChevronLeft, ChevronRight, ArrowRight, RotateCcw
} from 'lucide-react';

const ICON_MAP = { Battery, Leaf, Moon, Scale, Brain, Sparkles, Heart, Shield };

const SERVICES = [
  { id: 'fonksiyonel-tip', title: 'Fonksiyonel Tıp Danışmanlığı', color: '#1F6E7A', lightColor: '#1F6E7A20' },
  { id: 'hormonal-antiaging', title: 'Hormonal Antiaging', color: '#8B5E3C', lightColor: '#8B5E3C20' },
  { id: 'stres-yonetimi', title: 'Otonom Sağlık ve Stres Yönetimi', color: '#6B5B95', lightColor: '#6B5B9520' },
  { id: 'koruyucu-hekimlik', title: 'Koruyucu Hekimlik', color: '#2F7D6D', lightColor: '#2F7D6D20' },
  { id: 'yenileyici-uygulamalar', title: 'Yenileyici Uygulamalar', color: '#C06040', lightColor: '#C0604020' },
];

const SCALE_LABELS = ['', 'Hiç', 'Nadiren', 'Bazen', 'Sık sık', 'Sürekli'];

export const HealthAssessment = () => {
  const [symptoms, setSymptoms] = useState(DEFAULT_SYMPTOMS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [ratings, setRatings] = useState({});
  const [direction, setDirection] = useState(0);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    axios.get(`${API}/symptoms`).then(r => setSymptoms(r.data)).catch(console.error);
  }, []);

  const currentSymptom = symptoms[currentIndex];
  const totalRated = Object.keys(ratings).length;
  const progress = symptoms.length > 0 ? (totalRated / symptoms.length) * 100 : 0;

  /* ─── Calculate Scores ─── */
  /* Formula: weight/5 = unit, contribution = unit × rating
     Percentage = actual_score / max_possible_score × 100
     So if user rates all 1 → low %, all 5 → 100%. NOT normalized to sum=100. */
  const scores = useMemo(() => {
    const serviceIds = SERVICES.map(s => s.id);
    const maxPossible = {};
    serviceIds.forEach(id => { maxPossible[id] = 0; });
    symptoms.forEach((s) => {
      serviceIds.forEach(id => {
        maxPossible[id] += (s.weights?.[id] || 0);
      });
    });

    const raw = {};
    serviceIds.forEach(id => { raw[id] = 0; });
    symptoms.forEach((s) => {
      const r = ratings[s.id] || 0;
      if (r === 0) return;
      serviceIds.forEach(id => {
        raw[id] += ((s.weights?.[id] || 0) / 5) * r;
      });
    });

    const hasAny = Object.values(raw).some(v => v > 0);
    if (!hasAny) return SERVICES.map((s) => ({ ...s, score: 0, pct: 0 }));

    return SERVICES.map((s) => ({
      ...s,
      score: Math.round(raw[s.id] * 10) / 10,
      pct: maxPossible[s.id] > 0 ? Math.round((raw[s.id] / maxPossible[s.id]) * 100) : 0,
    })).sort((a, b) => b.pct - a.pct);
  }, [ratings, symptoms]);

  const handleRate = (value) => {
    setRatings((prev) => ({ ...prev, [currentSymptom.id]: value }));
    // Auto-advance after a short delay
    if (currentIndex < symptoms.length - 1) {
      setTimeout(() => {
        setDirection(1);
        setCurrentIndex((i) => i + 1);
      }, 300);
    } else {
      setTimeout(() => setShowResults(true), 300);
    }
  };

  const handleSkip = () => {
    // Remove any existing rating for this symptom (treat as 0)
    setRatings((prev) => {
      const next = { ...prev };
      delete next[currentSymptom.id];
      return next;
    });
    if (currentIndex < symptoms.length - 1) {
      setDirection(1);
      setCurrentIndex((i) => i + 1);
    } else {
      setShowResults(true);
    }
  };

  const goTo = (idx) => {
    setDirection(idx > currentIndex ? 1 : -1);
    setCurrentIndex(idx);
    setShowResults(false);
  };

  const reset = () => {
    setRatings({});
    setCurrentIndex(0);
    setShowResults(false);
  };

  if (!currentSymptom) return <div className="text-center py-8 text-[14px] text-[#5F6B76]">Yükleniyor...</div>;
  const IconComp = ICON_MAP[currentSymptom.icon] || Heart;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
      {/* When completed: single balanced row */}
      {showResults ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
          {/* Left: Completion Card */}
          <div className="bg-white rounded-[28px] border border-[#E7E5E4] p-8 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.1)] flex flex-col items-center justify-center text-center">
            <motion.div
              initial={{ scale: 0 }} animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 260, damping: 20 }}
              className="w-16 h-16 rounded-full bg-[#1F6E7A]/10 flex items-center justify-center mb-4"
            >
              <Heart size={28} className="text-[#1F6E7A]" />
            </motion.div>
            <h3 className="font-heading font-semibold text-[24px] mb-2">Değerlendirme Tamamlandı</h3>
            <p className="text-[14px] text-[#5F6B76] mb-6 max-w-xs">
              Yanıtlarınıza göre size en uygun tedaviyi sağ taraftaki sonuçlarda görebilirsiniz.
            </p>
            <Link
              to={`/hizmetler/${scores[0]?.id}`}
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#1F6E7A] text-white text-[14px] font-semibold hover:bg-[#185960] transition-colors shadow-[0_4px_12px_-4px_rgba(31,110,122,0.3)] mb-3"
              data-testid="health-assessment-result-cta"
            >
              En Uygun Tedaviyi İncele <ArrowRight size={16} />
            </Link>
            <button
              onClick={reset}
              className="flex items-center gap-1.5 text-[14px] text-[#5F6B76] hover:text-[#1F6E7A] transition-colors"
              data-testid="health-assessment-reset"
            >
              <RotateCcw size={12} /> Tekrar Değerlendir
            </button>
          </div>

          {/* Right: Scores — Racing Bar Chart */}
          <div className="bg-white rounded-[28px] border border-[#E7E5E4] p-8 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.08)] flex flex-col">
            <h3 className="font-heading font-semibold text-[22px] mb-1">Tedavi Uyumluluk Puanı</h3>
            <p className="text-[14px] text-[#5F6B76] mb-6">Yanıtlarınıza göre nihai sonuç</p>
            <div className="flex flex-col flex-1 justify-center" style={{ position: 'relative', minHeight: scores.length * 52 }}>
              {scores.map((svc, i) => (
                <motion.div
                  key={svc.id}
                  layout
                  transition={{ type: 'spring', stiffness: 200, damping: 25, mass: 0.8 }}
                  style={{ position: 'absolute', top: i * 52, left: 0, right: 0 }}
                >
                  <div className="flex items-center gap-3 h-[46px]">
                    <motion.span
                      layout
                      className="text-[14px] font-bold w-5 text-center shrink-0"
                      style={{ color: i === 0 ? svc.color : '#9CA3AF' }}
                    >
                      {i + 1}
                    </motion.span>
                    <div className="flex-1 flex items-center gap-3">
                      <div className="flex-1 h-[34px] rounded-lg bg-[#F5F3EF] overflow-hidden">
                        <motion.div
                          className="h-full rounded-lg flex items-center"
                          style={{ backgroundColor: svc.color }}
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.max(svc.pct, 5)}%` }}
                          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1], delay: i * 0.1 }}
                        >
                    <span className="text-[14px] font-semibold text-white pl-2.5 truncate whitespace-nowrap">
                            {svc.title}
                          </span>
                        </motion.div>
                      </div>
                      <motion.span
                        className="text-[14px] font-bold tabular-nums shrink-0 min-w-[38px] text-right"
                        style={{ color: svc.color }}
                        initial={{ scale: 1.4, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.4, delay: i * 0.1 + 0.3 }}
                      >
                        %{svc.pct}
                      </motion.span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <Link
              to={`/hizmetler/${scores[0]?.id}`}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-[#1F6E7A] text-white text-base font-semibold hover:bg-[#185960] transition-colors shadow-[0_4px_12px_-4px_rgba(31,110,122,0.3)] mt-6 text-center"
              data-testid="health-assessment-cta"
            >
              {scores[0]?.title} Tedavisini İncele <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      ) : (
      /* During quiz */
      <div>
        {/* Progress bar — above the grid */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[14px] font-medium text-[#5F6B76]">
              {totalRated} / {symptoms.length} belirti değerlendirildi
            </span>
            {totalRated > 0 && (
              <button
                onClick={reset}
                className="flex items-center gap-1 text-[14px] text-[#5F6B76] hover:text-[#1F6E7A] transition-colors"
              >
                <RotateCcw size={12} /> Sıfırla
              </button>
            )}
          </div>
          <div className="h-1.5 rounded-full bg-[#E7E5E4] overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-[#1F6E7A]"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />
          </div>
        </div>

        {/* Two cards side by side — same height */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">
          {/* Left: Question card (3 cols) */}
          <div className="lg:col-span-3 overflow-hidden">
            <AnimatePresence mode="wait" initial={false}>
              {!showResults ? (
                <motion.div
                  key={currentSymptom.id}
                  initial={{ opacity: 0, x: direction >= 0 ? 40 : -40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: direction >= 0 ? -40 : 40 }}
                  transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
                  className="bg-white rounded-[28px] border border-[#E7E5E4] p-6 sm:p-8 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.1)] h-full flex flex-col"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <motion.div
                      className="w-12 h-12 rounded-2xl bg-[#1F6E7A]/10 flex items-center justify-center"
                      whileHover={{ rotate: 8, scale: 1.1 }}
                    >
                      <IconComp size={24} className="text-[#1F6E7A]" />
                    </motion.div>
                    <div>
                      <h3 className="font-heading font-semibold text-[24px]">{currentSymptom.label}</h3>
                      <p className="text-[14px] text-[#5F6B76]">{currentIndex + 1} / {symptoms.length}</p>
                    </div>
                  </div>
                  <p className="text-[14px] text-[#5F6B76] leading-relaxed mb-6">
                    {currentSymptom.description}
                  </p>

                  {/* 1-5 Rating Slider */}
                  <div className="mt-auto">
                    <p className="text-[14px] font-medium text-[#5F6B76] mb-4">Bu belirtiyi ne sıklıkla yaşıyorsunuz?</p>
                    <div className="px-1">
                      <input
                        type="range"
                        min={1}
                        max={5}
                        step={1}
                        value={ratings[currentSymptom.id] || 3}
                        onChange={e => {
                          const val = parseInt(e.target.value);
                          setRatings(prev => ({ ...prev, [currentSymptom.id]: val }));
                        }}
                        className="w-full h-2 rounded-full appearance-none cursor-pointer accent-[#1F6E7A] bg-[#E7E5E4]"
                        style={{
                          background: `linear-gradient(to right, #1F6E7A 0%, #1F6E7A ${((ratings[currentSymptom.id] || 3) - 1) * 25}%, #E7E5E4 ${((ratings[currentSymptom.id] || 3) - 1) * 25}%, #E7E5E4 100%)`
                        }}
                        data-testid={`rating-slider-${currentSymptom.id}`}
                      />
                      <div className="flex justify-between mt-2">
                        {[1, 2, 3, 4, 5].map(val => (
                          <button
                            key={val}
                            onClick={() => setRatings(prev => ({ ...prev, [currentSymptom.id]: val }))}
                            className={`flex flex-col items-center gap-0.5 transition-colors ${
                              (ratings[currentSymptom.id] || 3) === val ? 'text-[#1F6E7A]' : 'text-[#9CA3AF]'
                            }`}
                            data-testid={`rating-${val}`}
                          >
                            <span className={`text-[14px] font-bold ${(ratings[currentSymptom.id] || 3) === val ? 'text-[#1F6E7A]' : ''}`}>{val}</span>
                            <span className="text-[14px] font-medium">{SCALE_LABELS[val]}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <motion.button
                        onClick={() => handleRate(ratings[currentSymptom.id] || 3)}
                        className="px-5 py-2 rounded-full bg-[#1F6E7A] text-white text-[14px] font-medium hover:bg-[#185960] transition-colors shadow-sm"
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        data-testid="rating-confirm"
                      >
                        Onayla &amp; Devam Et
                      </motion.button>
                    </div>
                  </div>

                  {/* Skip button */}
                  {currentSymptom.skippable && (
                    <div className="mt-3 text-center">
                      <button
                        onClick={handleSkip}
                        className="text-[14px] text-[#5F6B76] hover:text-[#1F6E7A] transition-colors underline underline-offset-2"
                      >
                        {currentSymptom.skipNote || 'Bu soruyu geç'}
                      </button>
                    </div>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="results"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white rounded-[28px] border border-[#E7E5E4] p-6 sm:p-8 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.1)] text-center h-full flex flex-col items-center justify-center"
                >
                  <div className="w-14 h-14 rounded-full bg-[#1F6E7A]/10 flex items-center justify-center mx-auto mb-4">
                    <Heart size={24} className="text-[#1F6E7A]" />
                  </div>
                  <h3 className="font-heading font-semibold text-[24px] mb-2">Değerlendirme Tamamlandı</h3>
                  <p className="text-[14px] text-[#5F6B76] mb-6">
                    Yanıtlarınıza göre size en uygun tedaviyi sağ taraftaki sonuçlarda görebilirsiniz.
                  </p>
                  <div className="flex flex-wrap justify-center gap-3">
                    <Link
                      to={`/hizmetler/${scores[0]?.id}`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1F6E7A] text-white text-[14px] font-semibold hover:bg-[#185960] transition-colors"
                    >
                      En Uygun Tedaviyi İncele <ArrowRight size={16} />
                    </Link>
                    <button
                      onClick={reset}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#E7E5E4] text-[14px] font-medium text-[#5F6B76] hover:bg-[#F5F3EF] transition-colors"
                    >
                      <RotateCcw size={14} /> Tekrar Değerlendir
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Right: Scoreboard card (2 cols) — Racing Bar Chart */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-[28px] border border-[#E7E5E4] p-5 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.08)] h-full flex flex-col">
            <h3 className="font-heading font-semibold text-[14px] mb-0.5">Tedavi Uyumluluk Puanı</h3>
            <p className="text-[14px] text-[#5F6B76] mb-4">Yanıtlarınıza göre anlık sonuç</p>

            {/* Racing bars — reorder with layout animation */}
            <div className="flex flex-col flex-1 justify-center" style={{ position: 'relative', minHeight: scores.length * 44 }}>
              {scores.map((svc, i) => (
                <motion.div
                  key={svc.id}
                  layout
                  transition={{ type: 'spring', stiffness: 200, damping: 25, mass: 0.8 }}
                  style={{ position: 'absolute', top: i * 44, left: 0, right: 0 }}
                  className="pr-1"
                >
                  <div className="flex items-center gap-2 h-[38px]">
                    {/* Rank number */}
                    <motion.span
                      layout
                      className="text-[14px] font-bold w-4 text-center shrink-0"
                      style={{ color: i === 0 ? svc.color : '#9CA3AF' }}
                    >
                      {i + 1}
                    </motion.span>
                    {/* Bar container */}
                    <div className="flex-1 flex items-center gap-2">
                      <div className="flex-1 h-[28px] rounded-lg bg-[#F5F3EF] overflow-hidden relative">
                        <motion.div
                          className="h-full rounded-lg flex items-center"
                          style={{ backgroundColor: svc.color }}
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.max(svc.pct, 4)}%` }}
                          transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
                        >
                          <span className="text-[12px] font-semibold text-white pl-2 truncate whitespace-nowrap">
                            {svc.title}
                          </span>
                        </motion.div>
                      </div>
                      {/* Score badge */}
                      <motion.span
                        className="text-[14px] font-bold tabular-nums shrink-0 min-w-[32px] text-right"
                        style={{ color: svc.color }}
                        key={`${svc.id}-${svc.pct}`}
                        initial={{ scale: 1.3, opacity: 0.5 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        %{svc.pct}
                      </motion.span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            {totalRated >= 3 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4"
              >
                <Link
                  to={`/hizmetler/${scores[0]?.id}`}
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-full bg-[#1F6E7A] text-white text-base font-semibold hover:bg-[#185960] transition-colors shadow-[0_4px_12px_-4px_rgba(31,110,122,0.3)] text-center"
                  data-testid="health-assessment-cta"
                >
                  {scores[0]?.title} Tedavisini İncele <ArrowRight size={16} />
                </Link>
              </motion.div>
            )}

          </div>
        </div>
        </div>

        {/* Navigation dots + arrows — below the grid */}
        <div className={`flex items-center justify-between mt-4 max-w-[60%] ${showResults ? 'invisible' : ''}`}>
          <motion.button
            onClick={() => goTo(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
            aria-label="Önceki soru"
            className="w-10 h-10 rounded-full border border-[#E7E5E4] flex items-center justify-center text-[#5F6B76] disabled:opacity-30 hover:bg-[#F5F3EF] transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronLeft size={18} />
          </motion.button>
          <div className="flex gap-1.5">
            {symptoms.map((s, i) => (
              <button
                key={s.id}
                onClick={() => goTo(i)}
                aria-label={`${i + 1}. soruya git`}
                className="w-6 h-6 flex items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1F6E7A]"
              >
                <span className={`rounded-full transition-all ${
                  i === currentIndex
                    ? 'bg-[#1F6E7A] w-6 h-2'
                    : ratings[s.id]
                    ? 'w-2 h-2 bg-[#1F6E7A]/40'
                    : 'w-2 h-2 bg-[#E7E5E4]'
                }`} />
              </button>
            ))}
          </div>
          <motion.button
            onClick={() => goTo(Math.min(symptoms.length - 1, currentIndex + 1))}
            disabled={currentIndex === symptoms.length - 1}
            aria-label="Sonraki soru"
            className="w-10 h-10 rounded-full border border-[#E7E5E4] flex items-center justify-center text-[#5F6B76] disabled:opacity-30 hover:bg-[#F5F3EF] transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronRight size={18} />
          </motion.button>
        </div>
      </div>
      )}
    </div>
  );
};
